import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  CredentialResponse,
  GoogleLogin,
  GoogleOAuthProvider,
} from "@react-oauth/google";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

export interface GoogleAuthProps {
    type: "login" | "register";
    onSuccess: (payload: {
        data: CredentialResponse;
        type: "login" | "register";
    }) => void;
    onError?: () => void;
    isLoading?: boolean;
}

const GoogleAuth: React.FC<GoogleAuthProps> = ({
    type,
    onSuccess,
    onError,
    isLoading = false,
}) => {
  const { t: myTranslate } = useTranslation();
  const [buttonWidth, setButtonWidth] = useState<number | string>("100%");
  const buttonContainerRef = useRef<HTMLDivElement | null>(null);

  // Extract the ID from environment variables
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Responsive button logic
  useEffect(() => {
    if (!clientId) return;

        const updateWidth = () => {
            if (buttonContainerRef.current) {
                const containerWidth =
                    buttonContainerRef.current.getBoundingClientRect().width;
                setButtonWidth(Math.min(Math.round(containerWidth), 400));
            }
        };

        updateWidth();

        const observer = new ResizeObserver(updateWidth);
        if (buttonContainerRef.current) {
            observer.observe(buttonContainerRef.current);
        }

        return () => observer.disconnect();
    }, [clientId]);

  // --- RENDER STATES ---

  // Error State (Missing ID in environment variables)
  if (!clientId) {
    return (
      <Box sx={{ mb: 3 }}>
        <Alert
          severity="error"
          className="notranslate"
          translate="no"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => window.location.reload()}
            >
              {myTranslate("common.retry")}
            </Button>
          }
        >
          {myTranslate("auth.unableToLoadGoogleSignIn")}
        </Alert>
      </Box>
    );
  }

    return (
        <Box sx={{ mb: 3 }} className="notranslate" translate="no">
            <Box
                ref={buttonContainerRef}
                className="notranslate"
                translate="no"
                sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    opacity: isLoading ? 0.5 : 1,
                }}
                aria-busy={isLoading}
                aria-disabled={isLoading}
            >
                <GoogleLogin
                    onSuccess={(data) => {
                        onSuccess({ data, type });
                    }}
                    onError={() => {
                        onError?.();
                    }}
                    theme="filled_blue"
                    size="large"
                    text={type === "login" ? "signin_with" : "signup_with"}
                    shape="rectangular"
                    width={buttonWidth.toString()}
                    locale="en"
                />

                {isLoading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            bgcolor: "rgba(255,255,255,0.6)",
                            zIndex: 10,
                            pointerEvents: "auto",
                            cursor: "not-allowed",
                        }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default GoogleAuth;
